import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { getMerchants, creditMerchantWallet, debitMerchantWallet } from '../../services/adminApi';
import { showLoadingToast, updateToast, showErrorToast } from '../../utils/toast';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../../utils/dateUtils';

const DirectRecharge = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [transactionResult, setTransactionResult] = useState(null);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  
  const [formData, setFormData] = useState({
    merchantId: '',
    transactionType: 'CREDIT',
    amount: '',
    referenceId: '',
    description: ''
  });

  useEffect(() => {
    loadMerchants();
  }, []);

  const loadMerchants = async () => {
    try {
      setLoading(true);
      const response = await getMerchants(1, 1000, 'all');
      
      let merchantList = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          merchantList = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          merchantList = response.data.content;
        }
      }
      setMerchants(merchantList);
    } catch (error) {
      console.error('Failed to load merchants:', error);
      showErrorToast('Failed to load merchants for selection');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.merchantId) {
      showErrorToast('Please select a merchant');
      return;
    }
    if (!formData.amount || isNaN(formData.amount) || Number(formData.amount) <= 0) {
      showErrorToast('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    const toastId = showLoadingToast('Processing transfer...');

    try {
      const remarks = formData.referenceId 
        ? `${formData.description} | Ref: ${formData.referenceId}` 
        : formData.description;
        
      const payload = {
        amount: Math.abs(Number(formData.amount)),
        remarks: remarks || formData.transactionType
      };

      let response;
      if (formData.transactionType === 'CREDIT') {
        response = await creditMerchantWallet(formData.merchantId, payload);
      } else {
        response = await debitMerchantWallet(formData.merchantId, payload);
      }

      updateToast(toastId, `Transfer processed successfully!`, 'success');
      
      const newTransaction = response.data || response;
      setTransactionResult(newTransaction);
      setTransactionHistory(prev => [newTransaction, ...prev]);
      setCurrentPage(0);
      
      // Reset form
      setFormData({
        merchantId: '',
        transactionType: 'CREDIT',
        amount: '',
        referenceId: '',
        description: ''
      });
    } catch (error) {
      console.error('Failed to process transfer:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to process transfer';
      updateToast(toastId, errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setTransactionResult(null);
    loadMerchants(); // Refresh merchant balances
  };

  if (loading && merchants.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-sm border-gray-200">
        <CardHeader className="pb-4 border-b mb-6 bg-white">
          <CardTitle className="text-xl font-bold">Direct Wallet Transfer</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">Select Merchant <span className="text-red-500">*</span></Label>
              <Select 
                value={formData.merchantId} 
                onValueChange={(val) => setFormData({...formData, merchantId: val})}
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue placeholder="Select merchant" />
                </SelectTrigger>
                <SelectContent>
                  {merchants.map(m => (
                    <SelectItem key={m.id || m.merchantId} value={m.merchantId || m.id}>
                      {m.merchantName || m.name} ({m.merchantId || m.id}) - Bal: ₹{m.walletBalance?.toFixed(2) || '0.00'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Amount (₹) <span className="text-red-500">*</span></Label>
                <Input 
                  type="number" 
                  placeholder="Enter amount" 
                  value={formData.amount} 
                  onChange={(e) => setFormData({...formData, amount: e.target.value})} 
                  className="bg-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Transaction Type <span className="text-red-500">*</span></Label>
                <div className="h-10 flex items-center bg-gray-50 px-4 rounded-md border">
                  <RadioGroup 
                    value={formData.transactionType} 
                    onValueChange={(val) => setFormData({...formData, transactionType: val})}
                    className="flex items-center space-x-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="CREDIT" id="r1" className="text-green-600 border-green-600" />
                      <Label htmlFor="r1" className="text-green-700 font-medium cursor-pointer">Credit (Add)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="DEBIT" id="r2" className="text-red-600 border-red-600" />
                      <Label htmlFor="r2" className="text-red-700 font-medium cursor-pointer">Debit (Deduct)</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Description / Remarks <span className="text-red-500">*</span></Label>
                <Input 
                  placeholder="Enter remarks" 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})} 
                  className="bg-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Transaction Reference</Label>
                <Input 
                  placeholder="Optional reference number" 
                  value={formData.referenceId} 
                  onChange={(e) => setFormData({...formData, referenceId: e.target.value})} 
                  className="bg-white"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className={`w-full text-white h-11 text-base font-semibold transition-colors shadow-sm ${
                formData.transactionType === 'CREDIT' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              disabled={submitting}
            >
              {submitting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
              {formData.transactionType === 'CREDIT' ? 'Process Credit' : 'Process Debit'}
            </Button>

          </form>
        </CardContent>
      </Card>

      {transactionHistory.length > 0 && (
        <Card className="shadow-sm border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between pb-4 border-b bg-white">
            <CardTitle className="text-lg font-bold">Transfer History</CardTitle>
            <div className="text-sm font-medium text-gray-500">{transactionHistory.length} total requests</div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50 hover:bg-gray-50/50">
                  <TableHead className="text-xs font-bold text-gray-500 h-10 tracking-wider">TXN ID</TableHead>
                  <TableHead className="text-xs font-bold text-gray-500 h-10 tracking-wider">TYPE</TableHead>
                  <TableHead className="text-xs font-bold text-gray-500 h-10 tracking-wider">AMOUNT</TableHead>
                  <TableHead className="text-xs font-bold text-gray-500 h-10 tracking-wider">BALANCES</TableHead>
                  <TableHead className="text-xs font-bold text-gray-500 h-10 tracking-wider">REFERENCE</TableHead>
                  <TableHead className="text-xs font-bold text-gray-500 h-10 tracking-wider">DATE</TableHead>
                  <TableHead className="text-xs font-bold text-gray-500 h-10 tracking-wider text-right pr-6">STATUS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactionHistory.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage).map((tx, idx) => (
                  <TableRow key={idx} className="hover:bg-gray-50/50 transition-colors">
                    <TableCell className="font-semibold text-gray-900 py-4">{tx.transactionId || 'N/A'}</TableCell>
                    <TableCell className="py-4">
                      {tx.transactionType === 'CREDIT' ? (
                         <span className="text-green-600 font-bold bg-green-50 px-2 py-1 rounded text-xs">CREDIT</span>
                      ) : (
                         <span className="text-red-600 font-bold bg-red-50 px-2 py-1 rounded text-xs">DEBIT</span>
                      )}
                    </TableCell>
                    <TableCell className="font-bold text-gray-900 py-4">₹{tx.amount?.toLocaleString()}</TableCell>
                    <TableCell className="py-4">
                      <div className="text-xs text-gray-500">Before: <span className="text-gray-900 font-medium">₹{tx.balanceBefore?.toLocaleString()}</span></div>
                      <div className="text-xs text-gray-500 mt-1">After: <span className="text-blue-600 font-medium">₹{tx.balanceAfter?.toLocaleString()}</span></div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="text-gray-900 font-medium">{tx.referenceId || '-'}</div>
                      <div className="text-xs text-gray-500 mt-1 truncate max-w-[150px]">{tx.description}</div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="text-gray-900 text-sm">{formatDateDDMMYYYY(tx.createdAt)}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{new Date(tx.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                    </TableCell>
                    <TableCell className="text-right py-4 pr-6">
                       <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-none shadow-none text-xs font-bold px-3 py-1">
                         SUCCESS
                       </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {Math.ceil(transactionHistory.length / itemsPerPage) > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t bg-white rounded-b-xl">
                <div className="text-sm text-gray-500">
                  Showing <span className="font-medium">{currentPage * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min((currentPage + 1) * itemsPerPage, transactionHistory.length)}</span> of <span className="font-medium">{transactionHistory.length}</span> results
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(transactionHistory.length / itemsPerPage) - 1, p + 1))}
                    disabled={currentPage === Math.ceil(transactionHistory.length / itemsPerPage) - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Success Modal */}
      <Dialog open={!!transactionResult} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-md border-0 shadow-lg">
          <DialogHeader className="pt-4">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 mb-4 shadow-sm">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <DialogTitle className="text-center text-2xl font-bold text-gray-900">Transfer Successful!</DialogTitle>
          </DialogHeader>
          
          <div className="bg-gray-50 p-5 rounded-xl space-y-3 mt-2 border border-gray-100">
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-500 text-sm font-medium">Transaction ID</span>
              <span className="font-mono text-gray-900 font-semibold bg-white px-2 py-1 rounded border border-gray-200 text-xs">{transactionResult?.transactionId || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-500 text-sm font-medium">Type</span>
              <span className={`font-bold ${transactionResult?.transactionType === 'CREDIT' ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'} px-2 py-1 rounded text-xs tracking-wide`}>
                {transactionResult?.transactionType || 'CREDIT'}
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-500 text-sm font-medium">Amount Processed</span>
              <span className="font-bold text-gray-900 text-lg">₹{transactionResult?.totalAmount?.toLocaleString() || '0.00'}</span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-500 text-sm font-medium">Wallet Before</span>
              <span className="font-semibold text-gray-700">₹{transactionResult?.balanceBefore?.toLocaleString() || '0.00'}</span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <span className="text-gray-500 text-sm font-medium">Wallet After</span>
              <span className="font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">₹{transactionResult?.balanceAfter?.toLocaleString() || '0.00'}</span>
            </div>
            <div className="flex justify-between items-center pt-1">
              <span className="text-gray-500 text-sm font-medium">Date</span>
              <span className="font-medium text-gray-700 text-sm">
                {transactionResult?.createdAt ? formatDateTimeDDMMYYYY(transactionResult.createdAt) : ''}
              </span>
            </div>
          </div>

          <DialogFooter className="sm:justify-center mt-6 pb-2">
            <Button onClick={handleCloseModal} className="w-full bg-gray-900 hover:bg-gray-800 h-11 font-semibold text-base">
              Close & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DirectRecharge;
